import React, { useEffect, useRef, useState } from 'react';
import { HouseKeepingInspectionModalState } from './types';

interface HouseKeepingInspectionModalProps {
  inspectionModal: HouseKeepingInspectionModalState | null;
  onPhotoChange: (
    photoType: keyof Omit<HouseKeepingInspectionModalState, 'orderId'>,
    file: File | null
  ) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const HouseKeepingInspectionModal: React.FC<HouseKeepingInspectionModalProps> = ({
  inspectionModal,
  onPhotoChange,
  onSubmit,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const steps = [
    { key: 'bedroom1Photo', label: 'غرفة النوم ١', hint: 'غرفة النوم الأولى ونظافتها' },
    { key: 'bedroom2Photo', label: 'غرفة النوم ٢', hint: 'غرفة النوم الثانية ونظافتها' },
    { key: 'bathroomPhoto', label: 'الحمام', hint: 'الحمام ونظافته' },
    { key: 'kitchenPhoto', label: 'المطبخ', hint: 'المطبخ ونظافته' },
    { key: 'livingAreaPhoto', label: 'غرفة المعيشة', hint: 'غرفة المعيشة ونظافتها' }
  ] as const;

  const allPhotos = steps.every((step) => inspectionModal?.[step.key]);
  const currentPhoto = inspectionModal?.[steps[currentStep].key];
  const currentPhotoKey = steps[currentStep].key;

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
    setIsVideoReady(false);
  };

  const startCamera = async () => {
    setCameraError(null);
    setIsVideoReady(false);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('الكاميرا غير مدعومة على هذا الجهاز');
      return;
    }

    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });

      streamRef.current = stream;
      setIsCameraOpen(true);
    } catch {
      setCameraError('تعذر فتح الكاميرا. تأكد من السماح باستخدامها');
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.videoWidth || !video.videoHeight) {
      setCameraError('انتظر حتى تصبح الكاميرا جاهزة');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const file = new File([blob], `${currentPhotoKey}-${Date.now()}.jpg`, {
          type: 'image/jpeg'
        });
        onPhotoChange(currentPhotoKey, file);
        stopCamera();
      },
      'image/jpeg',
      0.9
    );
  };

  const changeStep = (step: number) => {
    stopCamera();
    setCameraError(null);
    setCurrentStep(step);
  };

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!currentPhoto) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(currentPhoto);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [currentPhoto]);

  if (!inspectionModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl md:max-w-lg md:p-6 lg:max-w-xl lg:p-8">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-gilroy-semibold text-neutral-700 md:text-3xl lg:text-4xl">
              الفحص
            </h2>
            <span className="rounded-full bg-primary-100 px-3 py-1 text-sm font-gilroy-semibold text-primary-700 md:text-base">
              {currentStep + 1} / 5
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
            <div
              className="h-1.5 rounded-full bg-primary-500 transition-all"
              style={{ width: `${((currentStep + 1) / 5) * 100}%` }}
            />
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <h3 className="text-xl font-gilroy-semibold text-neutral-700 md:text-2xl lg:text-3xl">
            {steps[currentStep].label}
          </h3>
          <p className="mt-1 text-sm text-neutral-500 md:text-base lg:text-lg">
            {steps[currentStep].hint}
          </p>
        </div>

        <div>
          {isCameraOpen ? (
            <div className="rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 p-4">
              <video
                ref={(node) => {
                  videoRef.current = node;
                  if (node && streamRef.current) {
                    node.srcObject = streamRef.current;
                  }
                }}
                autoPlay
                muted
                playsInline
                onLoadedMetadata={() => setIsVideoReady(true)}
                className="h-64 w-full rounded-lg bg-black object-cover"
              />
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  className="flex-1 rounded-lg bg-primary-500 px-4 py-2.5 text-base font-gilroy-semibold text-white transition-colors hover:bg-primary-600"
                  disabled={!isVideoReady}
                  onClick={capturePhoto}
                >
                  التقاط الصورة
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-gray-200 px-4 py-2.5 text-base font-gilroy-semibold text-neutral-600 transition-colors hover:bg-gray-50"
                  onClick={stopCamera}
                >
                  إغلاق
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 p-4 transition-colors hover:bg-primary-100"
              onClick={startCamera}
            >
              {currentPhoto ? (
                <div className="w-full">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt={steps[currentStep].label}
                      loading="lazy"
                      className="mx-auto h-48 w-auto max-w-full rounded-lg object-contain"
                    />
                  )}
                  <span className="mt-3 block text-center text-base font-gilroy-semibold text-primary-700">
                    {currentPhoto.name}
                  </span>
                  <span className="mt-1 block text-center text-sm text-neutral-500">
                    اضغط لالتقاط صورة جديدة
                  </span>
                </div>
              ) : (
                <>
                  <span className="mb-2 text-4xl">📸</span>
                  <span className="text-center text-base font-gilroy-semibold text-primary-700">
                    التقط صورة
                  </span>
                </>
              )}
            </button>
          )}
          {cameraError && <p className="mt-2 text-center text-sm text-red-600">{cameraError}</p>}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 md:mt-8 md:gap-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-base font-gilroy-semibold text-neutral-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:text-lg lg:text-xl md:px-6 md:py-3"
            disabled={currentStep === 0}
            onClick={() => changeStep(Math.max(0, currentStep - 1))}
          >
            السابق
          </button>

          <button
            type="button"
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-base font-gilroy-semibold text-neutral-600 transition-colors hover:bg-gray-50 md:text-lg lg:text-xl md:px-6 md:py-3"
            onClick={() => {
              stopCamera();
              onCancel();
            }}
          >
            إلغاء
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              className="flex-1 rounded-lg border border-primary-300 bg-primary-50 px-4 py-2.5 text-base font-gilroy-semibold text-primary-700 transition-colors hover:bg-primary-100 md:text-lg lg:text-xl md:px-6 md:py-3"
              onClick={() => changeStep(currentStep + 1)}
            >
              التالي
            </button>
          ) : (
            <button
              type="button"
              className="rounded-lg border border-primary-300 bg-primary-50 px-4 py-2.5 text-base font-gilroy-semibold text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50 md:text-lg lg:text-xl md:px-6 md:py-3"
              disabled={!allPhotos}
              onClick={() => {
                stopCamera();
                onSubmit();
              }}
            >
              إرسال
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
